/**
 * A "tracked" job: one the seeker has viewed, saved, or applied to — the shape persisted in a
 * `viewedJob#{id}`/`savedJob#{id}`/`appliedJob#{id}` UserState entry (see
 * `utils/user-state/extract-jobs-from-user-state.ts`). Distinct from `types/job.ts`'s `Job`,
 * which is the raw Typesense search-result/listing shape — same name, different module, same
 * disambiguation-by-import-path convention already used elsewhere in this codebase.
 *
 * TEC-404: extracted out of `redux/features/jobs/jobsSlice.ts` so these types (and
 * `isChecklistItem`) have a Redux-free home; `jobsSlice.ts` re-exports them for now so existing
 * call sites are untouched, until it's deleted entirely once Phase C's Redux-dispatch removal
 * lands.
 */

import type { MailInterviewDate, PlusTrackerData } from './plus-myjobs';

// Mutually exclusive states: only one can be active at a time
export type ApplicationState = 'interviewing' | 'offer' | 'rejected' | 'hired' | null;

/**
 * How a job reached the Applied column (TEC-722): sent through autofill, confirmed by the user
 * after opening the employer's form, or marked by hand (kanban, SRP card). Entries written before
 * this field existed have none and read as `manual`.
 */
export type ApplySource = 'autofill' | 'external' | 'manual';

export function isApplySource(value: unknown): value is ApplySource {
  return value === 'autofill' || value === 'external' || value === 'manual';
}

/** A single follow-through subtask on an application's checklist (e.g. "Send thank-you note"). */
export interface ChecklistItem {
  id: string;
  text: string;
  done: boolean;
}

export function isChecklistItem(value: unknown): value is ChecklistItem {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return typeof v.id === 'string' && typeof v.text === 'string' && typeof v.done === 'boolean';
}

// Independent flags: can coexist with any mutually exclusive state
export interface Job {
  id: string;
  applicationState?: ApplicationState; // Mutually exclusive: interviewing, offer, rejected, hired, or null
  isHidden?: boolean; // Independent flag: can be true alongside any applicationState
  /** Pasted off-site application link (stored in user state); not an Algolia record */
  externalUrl?: string;
  /** Employer display name when known (e.g. from Algolia hit) */
  companyName?: string;
  /** ISO date string; drives the "Closes in N d" kanban chip. Only present if captured at save/apply time. */
  applicationDeadline?: string;
  /** Freeform private note (SEEKER_HUB_PLAN.md Phase 5 shared notes primitive) — same field shape as a followed company's notes. */
  notes?: string;
  /** Freeform categorization pills the seeker assigns (e.g. "dream job", "remote") — same shared-primitive pattern as notes. */
  tags?: string[];
  /** Per-application follow-through subtasks (e.g. "Send thank-you note", "Prep for interview"). */
  checklist?: ChecklistItem[];
  /** How the job was marked applied (TEC-722). */
  applySource?: ApplySource;
  /** ISO date until which the quiet-application nudge is snoozed ("Still waiting", TEC-723). */
  nudgeSnoozedUntil?: string;
  /** JobCrawls Plus tracker extras (TEC-744): follow-up date, interviews, contact, dated notes, offer details. One additive field. */
  plusTracker?: PlusTrackerData;
  /** Interview dates read from employer emails by the Applying area's ingest (written server-side; carried through client rewrites). */
  interviewDates?: MailInterviewDate[];
  [key: string]: unknown;
}

export interface JobsState {
  viewedJobs: Job[];
  savedJobs: Job[];
  appliedJobs: Job[];
}
